/**
 * topBarLayout — pure, Phaser-free geometry for the top-of-screen headers in TownScene
 * (the warm welcome banner) and RouteScene (the biome banner), where they share a top
 * strip with a top-right/top-corner button (Set out / Head home).
 *
 * A phone-viewport (390×844 portrait) audit found two overlaps:
 *   • Town: the full welcome line ("Welcome back, Wanderer. The tavern is lively
 *     tonight.") is too long on a narrow phone — it overflowed across the top and
 *     reached the "Set out ›" button (top-right) and the tavern crowd's name labels.
 *   • Route: the centered biome banner ("Sunny Meadow") overlapped the "Head home"
 *     button at top-right on the narrow width.
 *
 * The fix keeps DESKTOP layouts intact (roomy widths still get the full welcome line and
 * a centered banner) and only changes behaviour on a compact/phone viewport: a COMPACT
 * welcome line, a shrunk font, a width constrained to exclude the button's column, and —
 * for the route banner — a center that, if it would still graze the button, is pushed
 * down to its own clear band BELOW the button row. This module owns the maths; the scenes
 * apply it. Pure given the few inputs the layout snapshot exposes, so it is unit-testable
 * in the node env (RouteScene/TownScene import Phaser at module scope and can't load
 * there).
 */

/**
 * The town welcome line. On a roomy screen it carries the warm "the tavern is lively
 * tonight" tail; on a compact PHONE that long line overflowed into the Set out button +
 * the crowd labels, so we drop the tail and greet by name only — short enough to sit in
 * its own band clear of everything. `lively` reflects whether anyone is home (the scene
 * passes `here > 0`); when the tavern is empty BOTH forms are just the name greeting.
 */
export function townWelcomeText(name: string, lively: boolean, compact: boolean): string {
  if (compact || !lively) return `Welcome back, ${name}.`;
  return `Welcome back, ${name}. The tavern is lively tonight.`;
}

/**
 * The max width (px) the town welcome text may occupy so it never reaches the top-right
 * Set out button. The greeting is left-anchored at `safeX + leftPad`; the button's left
 * edge is `safeRight - rightPad - buttonWidth`. We cap the text width at the gap between
 * those, less a small breathing margin, and floor it at a sane minimum so a very narrow
 * phone still shows a readable (wrapping) line rather than collapsing to nothing. On a
 * roomy desktop the cap is large and the (single-line) greeting never hits it.
 */
export function townWelcomeMaxWidth(opts: {
  safeX: number;
  safeWidth: number;
  leftPad: number;
  rightPad: number;
  buttonWidth: number;
  margin: number;
  min: number;
}): number {
  const greetingLeft = opts.safeX + opts.leftPad;
  const buttonLeft = opts.safeX + opts.safeWidth - opts.rightPad - opts.buttonWidth;
  const available = buttonLeft - opts.margin - greetingLeft;
  return Math.max(opts.min, available);
}

/**
 * Horizontal span [left, right] of a center-anchored (origin 0.5) banner of `width`
 * centered at `centerX`. Used to test whether the route biome banner would reach the
 * top-right Head home button.
 */
export function bannerSpan(centerX: number, width: number): { left: number; right: number } {
  return { left: centerX - width / 2, right: centerX + width / 2 };
}

/**
 * Where to seat the route's biome banner so it never overlaps the top-right Head home
 * button. The banner is center-anchored (origin 0.5, 0) at `centerX`. If, at its measured
 * `bannerWidth`, its right edge would reach within `margin` of the button's left edge, we
 * push the banner DOWN into its own clear band BELOW the button row (so the two stack
 * vertically instead of colliding) and keep it at the top otherwise. Returns the banner's
 * top y. Desktop: the roomy width leaves the centered banner well clear of the corner
 * button, so it stays at the top row (no change).
 */
export function routeBannerTop(opts: {
  centerX: number;
  bannerWidth: number;
  topY: number;
  buttonLeft: number;
  buttonBottom: number;
  margin: number;
  dropGap: number;
}): number {
  const span = bannerSpan(opts.centerX, opts.bannerWidth);
  const reachesButton = span.right + opts.margin > opts.buttonLeft;
  return reachesButton ? opts.buttonBottom + opts.dropGap : opts.topY;
}
